import React from "react";

export interface IShowcaseAxisValue {
  value: string;
  label?: string;
}

const toAxis = (raw: (string | IShowcaseAxisValue)[]): IShowcaseAxisValue[] =>
  raw.map((entry: string | IShowcaseAxisValue): IShowcaseAxisValue =>
    typeof entry === "string" ? { value: entry, label: entry } : entry
  );

export interface IVariantGridBaseProps {
  label: string;
  caption?: string;
  cellMinWidth?: number;
  align?: "start" | "center" | "stretch" | "baseline";
}

export interface IVariantGrid1DProps extends IVariantGridBaseProps {
  cols: (string | IShowcaseAxisValue)[];
  rows?: never;
  render: (col: string) => React.ReactNode;
}

export interface IVariantGrid2DProps extends IVariantGridBaseProps {
  cols: (string | IShowcaseAxisValue)[];
  rows: (string | IShowcaseAxisValue)[];
  render: (row: string, col: string) => React.ReactNode;
}

export type IVariantGridProps = IVariantGrid1DProps | IVariantGrid2DProps;

const is2D = (props: IVariantGridProps): props is IVariantGrid2DProps => "rows" in props && Array.isArray(props.rows);

export const VariantGrid: React.FC<IVariantGridProps> = (props: IVariantGridProps): JSX.Element => {
  const { label, caption, cellMinWidth = 140, align = "center" } = props;
  const cols: IShowcaseAxisValue[] = toAxis(props.cols);
  const alignClass: string = `lab-grid--align-${align}`;

  if (is2D(props)) {
    const rows: IShowcaseAxisValue[] = toAxis(props.rows);
    const templateColumns: string = `120px repeat(${cols.length}, minmax(${cellMinWidth}px, 1fr))`;

    return (
      <section className={`lab-grid lab-grid--2d ${alignClass}`}>
        <header className="lab-grid__head">
          <span className="lab-grid__label">{label}</span>
          {caption && <span className="lab-grid__caption">{caption}</span>}
        </header>
        <div className="lab-grid__matrix" style={{ gridTemplateColumns: templateColumns }}>
          <div className="lab-grid__corner" />
          {cols.map((col: IShowcaseAxisValue) => (
            <div key={`col-${col.value}`} className="lab-grid__col-head">
              {col.label ?? col.value}
            </div>
          ))}
          {rows.map((row: IShowcaseAxisValue) => (
            <React.Fragment key={`row-${row.value}`}>
              <div className="lab-grid__row-head">{row.label ?? row.value}</div>
              {cols.map((col: IShowcaseAxisValue) => (
                <div key={`cell-${row.value}-${col.value}`} className="lab-grid__cell">
                  {props.render(row.value, col.value)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </section>
    );
  }

  const templateColumns: string = `repeat(${cols.length}, minmax(${cellMinWidth}px, 1fr))`;

  return (
    <section className={`lab-grid lab-grid--1d ${alignClass}`}>
      <header className="lab-grid__head">
        <span className="lab-grid__label">{label}</span>
        {caption && <span className="lab-grid__caption">{caption}</span>}
      </header>
      <div className="lab-grid__matrix" style={{ gridTemplateColumns: templateColumns }}>
        {cols.map((col: IShowcaseAxisValue) => (
          <div key={`col-${col.value}`} className="lab-grid__col-head">
            {col.label ?? col.value}
          </div>
        ))}
        {cols.map((col: IShowcaseAxisValue) => (
          <div key={`cell-${col.value}`} className="lab-grid__cell">
            {props.render(col.value)}
          </div>
        ))}
      </div>
    </section>
  );
};
